import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router';
import { BidraMark, IconBurger, IconClose, IconMoon, IconSun } from '../ui/icons';
import { useTheme } from '../ui/theme-toggle';
import { useSession } from '../auth/session';
import { K } from '../kabinet/atoms';

/**
 * Разделы кабинета — ящиком слева, как в эталоне.
 *
 * ПОЧЕМУ ЯЩИК, А НЕ СПИСОК ИЗ-ПОД КНОПКИ. Список на 220 пикселей
 * показывал два слова и больше ничего: ни в каком ты кабинете, ни что
 * в разделе лежит, ни куда ещё можно уйти. Всё это человек собирал
 * глазами по шапке — а в шапке на это места нет, и часть переехала
 * в значки. В эталоне бургер открывает ящик во всю высоту: знак
 * и кабинет сверху, разделы с подписями, отбивка, «Кабинет» — сменить,
 * пароль, сборка, — и внизу тема с выходом. Один жест вместо четырёх
 * мест, в которых надо знать, что искать.
 *
 * ПОДПИСЬ ПОД НАЗВАНИЕМ РАЗДЕЛА — НЕ УКРАШЕНИЕ. «Что сделано» и «Биддер»
 * ничего не говорят человеку, зашедшему второй раз; «Таблица кампаний
 * и деньги» и «Что сервис сделал бы» говорят.
 *
 * ЗАКРЫВАЕТСЯ ПО Esc, ПО ЩЕЛЧКУ МИМО, ПО КРЕСТИКУ И ПРИ ПЕРЕХОДЕ.
 * Ящик без выхода — ловушка; ящик, оставшийся открытым поверх того,
 * куда ты только что перешёл, — тоже.
 *
 * РИСУЕТСЯ В КОРНЕ СТРАНИЦЫ. Внутри шапки он обрезался бы её высотой
 * в пятьдесят два пикселя — та же ошибка, что была с увеличением
 * фотографий.
 */

export interface Section {
  to: string;
  title: string;
  /**
   * Значок слева от названия.
   *
   * ═══════════════════════════════════════════════════════════════════
   * ЗНАЧОК У КАЖДОГО ПУНКТА — ТРЕБОВАНИЕ ПАКЕТА 25.08, И ОНО НЕ ПРО
   * КРАСОТУ.
   *
   * Меню открывают не читая: рука тянется к тому месту, где вчера
   * был нужный пункт. Значок держит это место, когда подпись
   * поменялась, а список из четырёх одинаковых строк — нет.
   * ═══════════════════════════════════════════════════════════════════
   */
  icon?: React.ReactNode;
  /** Одна строка о том, что в разделе лежит. */
  sub?: string;
  /** Точное совпадение адреса — для корневого раздела. */
  end?: boolean;
  /**
   * Раздел куплен, но экранов у него ещё нет.
   *
   * ═══════════════════════════════════════════════════════════════════
   * ПОКАЗАН, НО НЕ ССЫЛКА — И ЭТО НЕ ПОЛУМЕРА.
   *
   * Раздел входит в тариф и назван в карточке, за которую заплатили.
   * Спрятать его из меню значило бы продать невидимое: человек ищет
   * оплаченное и не находит, а служба поддержки объясняет, что оно
   * есть, просто не показывается.
   *
   * Сделать ссылкой — хуже: пустой экран без объяснения читается как
   * поломка, и о ней напишут в тот же день. Поэтому строка на месте,
   * рядом сказано «раздел готовится», и нажать её нельзя.
   * ═══════════════════════════════════════════════════════════════════
   */
  soon?: boolean;
}

/** Группа пунктов с заголовком: «Разделы сервиса», «Пространство». */
export interface Group {
  caption: string;
  items: Section[];
}

export function Menu({
  groups,
  current,
  cabinet,
  onOpenChange,
}: {
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ДВЕ ГРУППЫ ВМЕСТО СПИСКА И «ЕЩЁ» — ПАКЕТ 25.08, РАЗДЕЛ 1.
   *
   * Было: восемь пунктов подряд, среди них «Запросы», «Намерения»
   * и «Правила». Их убрали из меню нарочно, и довод дизайнера
   * дословный: это части работы с КАМПАНИЕЙ, а не разделы сервиса.
   * Открываются они с карточки кампании — вкладками и «Возможностями».
   *
   * Человек, пришедший в «Запросы» из меню, попадал туда без кампании
   * и видел пустой экран с отбором, который нечем наполнить.
   *
   * «Пароль и доступ» и «Сборка» тоже ушли — в меню профиля.
   * ═══════════════════════════════════════════════════════════════════
   */
  groups: Group[];
  current: string;
  /** Имя кабинета под названием сервиса — «где я» одной строкой. */
  cabinet?: string;
  /** Разрешает рамке читать подписи только для открытого меню. */
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const { dark, toggle } = useTheme();
  const { signOut } = useSession();
  const location = useLocation();

  useEffect(() => onOpenChange?.(open), [open, onOpenChange]);

  // ПЕРЕХОД ЗАКРЫВАЕТ ЯЩИК САМ. Полагаться на `onClick` у каждого пункта
  // значит однажды завести пункт без него — и ящик останется висеть
  // поверх нового экрана.
  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', esc);
    const was = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', esc);
      document.body.style.overflow = was;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        data-d="кнопка-разделы"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Разделы"
        title={`Разделы. Сейчас: ${current}`}
        onClick={() => setOpen(true)}
        className={K.кнопкаРазделы}
      >
        <IconBurger />
      </button>

      {open &&
        createPortal(
          /* ОБЛАСТЬ И ЧАСТИЦА — НА РАЗНЫХ УЗЛАХ. Правило частицы
             записано как `[data-kab] .k-подложка-меню`, то есть
             ПОТОМОК области: узел, на котором висит и то и другое,
             сам себе не потомок, и правило не применяется вовсе.
             Внешний блок — область, внутренний — подложка. */
          <div data-kab>
          <div className={K.подложкаМеню} onClick={() => setOpen(false)}>
            <div
              role="menu"
              aria-label="Разделы"
              className={K.ящикМеню}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={K.шапкаЯщикаМеню}>
                <BidraMark size={28} />
                <span className={K.столбикВЯщикеМеню}>
                  <b style={{ letterSpacing: '-.01em' }}>БИДРА</b>
                  {cabinet && <span className={K.кабинетВЯщикеМеню}>{cabinet}</span>}
                </span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Закрыть меню"
                  title="Закрыть (Esc)"
                  className={K.кнопкаЗакрытьМеню}
                >
                  <IconClose size={14} />
                </button>
              </div>

              {/* ═══════════════════════════════════════════════════════
                  РАЗМЕТКА ЭТАЛОНА, А НЕ ПОХОЖАЯ НА НЕЁ.

                  `разделы-меню` — сам список; `пункт-меню` — строка
                  со значком, названием и подписью под ним. Величины
                  режет сборщик частиц прямо из эталона: поле 9×12,
                  зазор 12, полоска слева `--rail` у текущего пункта.

                  ЗАГОЛОВОК ГРУППЫ И ЕЁ ПУНКТЫ ЛЕЖАТ ОДНИМ УРОВНЕМ,
                  а не заголовок-обёртка вокруг пунктов. Так в эталоне,
                  и не случайно: зазор между строками задан один раз
                  на весь список (`gap: 2`), и обёртка вокруг группы
                  сломала бы его ровно на границе групп.
                  ═══════════════════════════════════════════════════ */}
              <div data-d="разделы-меню" className={K.разделыМеню}>
                {groups.map((g, gi) => (
                  <Fragment key={g.caption}>
                    {/* ОБЁРТКА У КАЖДОЙ ГРУППЫ, включая первую: так
                        в эталоне. У первой внутри одна подпись,
                        у следующих — черта и подпись, и подпись у них
                        БЕЗ верхнего поля: зазор держит черта. */}
                    <span className={K.группаМеню}>
                      {gi > 0 && <span aria-hidden="true" className={K.чертаГруппыМеню} />}
                      <span className={gi === 0 ? K.подписьГруппыМеню : K.подписьГруппыМенюДалее}>
                        {g.caption}
                      </span>
                    </span>

                    {g.items.map((s) =>
                      s.soon === true ? (
                        /* НЕ ССЫЛКА И НЕ КНОПКА: нажимать нечего,
                           и вид обязан сказать это раньше пальца.
                           Метка «в разработке» стоит рядом с названием,
                           как в эталоне, — а не хвостом «— скоро». */
                        <div
                          key={s.to}
                          data-d="пункт-меню"
                          role="menuitem"
                          aria-disabled="true"
                          className={K.пунктМеню}
                          title="Раздел готовится"
                        >
                          <ЧастиПункта s={s} тихо />
                        </div>
                      ) : (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          end={s.end}
                          data-d="пункт-меню"
                          role="menuitem"
                          className={({ isActive }) =>
                            `${isActive ? K.пунктМенюТекущий : K.пунктМеню} no-underline`
                          }
                        >
                          {({ isActive }) => <ЧастиПункта s={s} текущий={isActive} />}
                        </NavLink>
                      ),
                    )}
                  </Fragment>
                ))}
              </div>

              {/* ═══════════════════════════════════════════════════════
                  ПОДВАЛ ЯЩИКА — «ТЁМНАЯ ТЕМА» И «ВЫЙТИ».

                  Он есть в эталоне и пропал у нас 25.08: разметку меню
                  переписали, а сверять подвал было нечем — своего
                  `data-d` у него нет, и замер его не видит. Заметил
                  владелец, глядя на снимок рядом.

                  ПОЧЕМУ ЭТО НЕ ПОВТОР МЕНЮ ПРОФИЛЯ. На узком экране
                  профиль в шапку не помещается, а выйти и переключить
                  тему нужно и там. В эталоне оба места есть
                  одновременно, и это не оплошность автора: ящик —
                  единственное меню, которое на 390 открывается.
                  ═══════════════════════════════════════════════════ */}
              <div className={K.подвалМеню}>
                <button
                  type="button"
                  className={K.кнопкаТемыВМеню}
                  onClick={() => {
                    toggle();
                    setOpen(false);
                  }}
                >
                  {dark ? <IconSun size={15} /> : <IconMoon size={15} />}
                  {dark ? 'Светлая тема' : 'Тёмная тема'}
                </button>
                <button type="button" className={K.кнопкаВыйтиВМеню} onClick={() => void signOut()}>
                  Выйти
                </button>
              </div>
            </div>
          </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Внутренности пункта: значок, название с меткой и подпись.
 *
 * Вынесено отдельно потому, что пункт бывает ссылкой и бывает
 * неподвижным `div` — а внутри у них одно и то же. Два раза
 * написанное одно и то же разъезжается на первой правке; здесь
 * это уже случилось однажды с меткой «— скоро», которая стояла
 * только у неподвижного.
 */
function ЧастиПункта({
  s,
  тихо,
  текущий,
}: {
  s: Section;
  тихо?: boolean;
  текущий?: boolean;
}) {
  /* ═══════════════════════════════════════════════════════════════════
     ВИД ВЫБИРАЕТСЯ ЗДЕСЬ, А НЕ ПРАВИЛОМ В CSS.

     Эталон рисует текущий пункт встроенным стилем на тех же узлах —
     значит и различие живёт в разметке. Частицы вырезаны из эталона
     все три (`k-название-пункта`, `-текущее`, `-скоро`), и правило
     вида `.k-пункт-меню-текущий .k-название-пункта{…}` пришлось бы
     писать руками — а `atoms.css` собран и сверяется побайтно.
     ═══════════════════════════════════════════════════════════════════ */
  const название = тихо === true
    ? K.названиеПунктаСкоро
    : текущий === true
      ? K.названиеПунктаТекущее
      : K.названиеПункта;

  return (
    <>
      <span
        data-d="значок-раздела"
        className={текущий === true ? K.значокРазделаТекущий : K.значокРаздела}
      >
        {s.icon}
      </span>
      <span className={K.колонкаПункта}>
        <span className={K.рядПункта}>
          <span className={название}>{s.title}</span>
          {тихо === true && (
            <span data-d="метка-скоро" className={K.меткаСкоро}>
              в разработке
            </span>
          )}
        </span>
        {s.sub !== undefined && (
          <span className={текущий === true ? K.подписьПунктаТекущая : K.подписьПункта}>
            {s.sub}
          </span>
        )}
      </span>
    </>
  );
}

